import { useDispatch, useSelector } from 'react-redux';
import { selectActivePokemon, selectIsMainPhase, selectOptions } from '../ActiveGame/selectors';
import { optionReason } from '../ActiveGame/options';
import { putActivePokemon, switchIn } from '../Pokemon/actions';
import { retreat } from '../RetreatButton/actions';
import { selectRetreatFlowing } from '../RetreatButton/selectors';
import { startRetreat } from '../RetreatButton/startRetreat';
import { selectCurrentUser } from '../Login/selectors';
import { selectBoardChoice, SWITCH_IN } from './selectors';
import { canRetreat, FINISH_RETREAT, PROMOTE, RETREAT, SWITCH_IN_OWED, upToActive } from './pokemonDrags';

/**
 * Where your Pokémon in play can be dragged right now, and what a drop does (pokemonDrags), for `userId`'s side of the
 * board. On the opponent's side nothing drags.
 *
 * @returns {{ activeDrags: boolean, up: string|null, retreatTo: function, goUp: function }}
 *   whether the active Pokémon can be dragged to the bench; what a benched one going up does; and the drops, which
 *   return false when something is asked first (the energy to discard)
 */
export default function usePokemonDrags(userId) {
  const dispatch = useDispatch();
  const mine = useSelector(selectCurrentUser).get('id') === userId;
  const activePokemon = useSelector((state) => selectActivePokemon(state, { userId }));
  const options = useSelector(selectOptions);
  const isMainPhase = useSelector((state) => selectIsMainPhase(state, { userId }));
  const boardChoice = useSelector(selectBoardChoice);
  const retreatFlowing = useSelector(selectRetreatFlowing);

  const retreatNow = mine && !!activePokemon && canRetreat({
    isMainPhase,
    retreatReason: optionReason(options && options.get('retreat')),
    boardChoice,
    retreatFlowing,
  });
  const up = mine && (boardChoice === null || boardChoice === SWITCH_IN)
    ? upToActive({
      hasActive: !!activePokemon,
      switchInOwed: !!options && options.get('switch_in') === true,
      pickingSwitchIn: boardChoice === SWITCH_IN,
      canRetreatNow: retreatNow,
    })
    : null;

  const retreatTo = (benchPosition) => startRetreat(
    dispatch, userId, activePokemon.getIn(['card', 'retreat_cost']) || 0, activePokemon.get('energyAttachments'), benchPosition,
  );

  const goUp = (how, benchPosition) => {
    if (how === PROMOTE) {
      dispatch(putActivePokemon(benchPosition));
    } else if (how === SWITCH_IN_OWED) {
      dispatch(switchIn(benchPosition));
    } else if (how === FINISH_RETREAT) {
      dispatch(retreat(benchPosition));
    } else if (how === RETREAT) {
      return retreatTo(benchPosition);
    }
    return true;
  };

  return { activeDrags: retreatNow, up, retreatTo, goUp };
}
